/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

/**
 * InvestorAccessGate — passcode screen before the pitch deck.
 * Stores access in sessionStorage so reloads don't re-prompt.
 */
import { useState } from "react";
import { motion } from "framer-motion";
import { Lock, ArrowRight, ShieldCheck } from "lucide-react";
import PaxiaLogo from "../../assets/paxia logo 3- sillvery - no bg.png";

export default function InvestorAccessGate({ correctCode, onGranted }) {
  const [code, setCode] = useState("");
  const [error, setError] = useState(false);
  const [shake, setShake] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (code.trim().toLowerCase() === correctCode.toLowerCase()) {
      sessionStorage.setItem("investor_access", "1");
      onGranted();
    } else {
      setError(true);
      setShake(true);
      setTimeout(() => setShake(false), 600);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0F1A] flex items-center justify-center px-4">
      {/* Gradient orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#3B82F6]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#4FA9A1]/10 rounded-full blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: "easeOut" }}
        className={`relative z-10 max-w-md w-full ${shake ? "animate-shake" : ""}`}
      >
        <div className="bg-[#0F1826]/80 backdrop-blur-xl border border-white/10 rounded-3xl p-8 sm:p-10 shadow-2xl">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <img src={PaxiaLogo} alt="Paxia" className="h-10 opacity-90" />
          </div>

          {/* Lock icon */}
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#3B82F6]/20 to-[#4FA9A1]/20 border border-white/10 flex items-center justify-center">
              <Lock className="w-7 h-7 text-[#60A5FA]" />
            </div>
          </div>

          <h1 className="text-2xl font-bold text-white text-center mb-2">
            Investor Access
          </h1>
          <p className="text-base text-gray-400 text-center mb-8">
            This presentation is confidential. Please enter the access code provided to you.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="relative">
              <input
                type="text"
                value={code}
                onChange={(e) => {
                  setCode(e.target.value);
                  setError(false);
                }}
                placeholder="Enter access code"
                autoFocus
                className={`w-full bg-[#0A0F1A]/60 border ${
                  error ? "border-red-500/60" : "border-white/15"
                } rounded-xl px-4 py-3.5 text-white placeholder-gray-500 focus:outline-none focus:ring-2 ${
                  error ? "focus:ring-red-500/40" : "focus:ring-[#3B82F6]/40"
                } transition-all text-center text-lg tracking-widest font-mono`}
              />
              {error && (
                <motion.p
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-red-400 text-xs mt-2 text-center"
                >
                  Invalid access code. Please try again.
                </motion.p>
              )}
            </div>

            <button
              type="submit"
              className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-[#3B82F6] to-[#4FA9A1] text-white font-semibold py-3.5 rounded-xl hover:opacity-90 transition-opacity"
            >
              <ShieldCheck className="w-5 h-5" />
              Access Presentation
              <ArrowRight className="w-4 h-4" />
            </button>
          </form>

          <p className="text-[10px] text-gray-400 text-center mt-6">
            Confidential & proprietary. Not for redistribution.
          </p>
        </div>
      </motion.div>

      {/* Shake animation */}
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
          20%, 40%, 60%, 80% { transform: translateX(4px); }
        }
        .animate-shake { animation: shake 0.6s ease-in-out; }
      `}</style>
    </div>
  );
}
